import type { PlanEntry } from '@agentclientprotocol/sdk/experimental/v2'
import { useBridge } from '@web/adapter/useBridgeRuntime'
import { ExtensionActions } from '@web/components/ExtensionActions'
import { Markdown } from '@web/components/Markdown'
import { SessionCost } from '@web/components/SessionCost'
import { StructuredValue } from '@web/components/tools/StructuredContent'
import { executionTitle } from '@web/lib/executionMode'
import {
  agentMetadata,
  conversationProvenance,
  terminalText,
} from '@web/state/acp'
import { useState } from 'react'
import '@web/components/agent-controls.css'

/** Inspect provider-neutral session state and display-only agent terminals. */
export function InfoPanel({ embedded = false }: { embedded?: boolean }) {
  const { snapshot, sessionId } = useBridge()
  const { acp, bridge } = snapshot
  const [tab, setTab] = useState<'session' | 'plans' | 'terminals' | 'debug'>(
    'session',
  )
  const metadata = agentMetadata(acp)
  const Panel = embedded ? 'div' : 'aside'
  return (
    <Panel
      className={`agent-details flex shrink-0 flex-col ${embedded ? 'border-t border-line bg-inset' : 'border-l border-line bg-panel'}`}
      {...(!embedded ? { 'aria-label': 'Session details' } : {})}
    >
      <div
        className="flex border-b border-line text-xs"
        role="group"
        aria-label="Details sections"
      >
        {(['session', 'plans', 'terminals', 'debug'] as const).map((name) => (
          <button
            key={name}
            aria-pressed={tab === name}
            className={`flex-1 border-b-2 px-2 py-2 capitalize ${tab === name ? 'border-accent text-fg' : 'border-transparent text-faint'}`}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3 text-xs">
        {tab === 'session' && (
          <>
            <dl className="space-y-3 break-words">
              <div>
                <dt className="text-faint">Provider</dt>
                <dd>{bridge.record?.config.provider ?? 'Agent'}</dd>
              </div>
              <div>
                <dt className="text-faint">Execution</dt>
                <dd>
                  {executionTitle(
                    bridge.record?.config.execution_mode ??
                      metadata.executionMode,
                    bridge.record?.config.provider,
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-faint">Provider session</dt>
                <dd className="mono">
                  {acp.providerSessionId ??
                    bridge.record?.config.provider_session_id ??
                    '—'}
                </dd>
              </div>
              {metadata.workspace && (
                <div>
                  <dt className="text-faint">Project</dt>
                  <dd>{bridge.record?.project || 'Blank workspace'}</dd>
                </div>
              )}
              <div>
                <dt className="text-faint">Foreground</dt>
                <dd>
                  {acp.foreground}
                  {acp.stopReason ? ` · ${acp.stopReason}` : ''}
                </dd>
              </div>
            </dl>
            <div className="my-4">
              <SessionCost sessionId={sessionId} total={bridge.totalCostUsd} />
            </div>
            {acp.usage && (
              <p className="my-3 text-muted">
                Context: {acp.usage.used.toLocaleString()} /{' '}
                {acp.usage.size.toLocaleString()} tokens
              </p>
            )}
            {acp.turnUsage && (
              <details>
                <summary>Last completed turn usage</summary>
                <StructuredValue value={acp.turnUsage} />
              </details>
            )}
            {acp.extensions
              .filter((item) => item.payload.type === 'agent/diagnostic')
              .map((item) => (
                <p key={item.seq} className="my-3 text-warn">
                  {String(
                    item.payload.message ??
                      'Saved history requires inspection.',
                  )}{' '}
                  <span className="text-faint">
                    Original position:{' '}
                    {String(item.payload.source_sequence ?? item.seq)}
                  </span>
                </p>
              ))}
            <ExtensionActions />
            {!!acp.configOptions.length && (
              <div className="mt-4">
                <h3 className="label">Current configuration</h3>
                {acp.configOptions.map((option) => (
                  <div key={option.configId} className="mt-2">
                    <span className="text-faint">{option.name}: </span>
                    {String(option.currentValue)}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {tab === 'plans' &&
          (Object.values(acp.plans).length ? (
            Object.values(acp.plans).map((plan) => (
              <section className="mb-4" key={plan.planId}>
                <h3 className="label mb-2">Plan {plan.planId}</h3>
                {plan.type === 'items' && Array.isArray(plan.entries) ? (
                  <ol className="space-y-2">
                    {(plan.entries as PlanEntry[]).map((entry, index) => (
                      <li
                        key={`${plan.planId}:${index}`}
                        className="flex items-start gap-2"
                      >
                        <span
                          aria-label={entry.status}
                          className={
                            entry.status === 'completed'
                              ? 'text-ok'
                              : 'text-muted'
                          }
                        >
                          {entry.status === 'completed'
                            ? '✓'
                            : entry.status === 'cancelled'
                              ? '–'
                              : entry.status === 'in_progress'
                                ? '◉'
                                : '○'}
                        </span>
                        <span
                          className={
                            entry.status === 'cancelled'
                              ? 'text-muted line-through'
                              : ''
                          }
                        >
                          {entry.content}
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : plan.type === 'markdown' &&
                  typeof plan.content === 'string' ? (
                  <Markdown text={plan.content} />
                ) : plan.type === 'file' && typeof plan.uri === 'string' ? (
                  <p className="break-all">{plan.uri}</p>
                ) : (
                  <StructuredValue value={plan} />
                )}
              </section>
            ))
          ) : (
            <p className="text-muted">No plans have been reported.</p>
          ))}
        {tab === 'terminals' &&
          (Object.values(acp.terminals).length ? (
            Object.values(acp.terminals).map((terminal) => (
              <section className="mb-4" key={terminal.terminalId}>
                {conversationProvenance(terminal._meta) && (
                  <p className="text-xs text-muted">
                    {conversationProvenance(terminal._meta)?.name ?? 'Subagent'}{' '}
                    · {conversationProvenance(terminal._meta)?.sessionId}
                  </p>
                )}
                <h3 className="font-medium">
                  {terminal.command ?? terminal.terminalId}
                </h3>
                <p className="my-1 text-faint">
                  {terminal.exitStatus
                    ? `Exited${terminal.exitStatus.exitCode != null ? ` · ${terminal.exitStatus.exitCode}` : ''}`
                    : 'Agent-owned terminal'}
                  {terminal.cwd ? ` · ${terminal.cwd}` : ''}
                </p>
                <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-control bg-inset p-2">
                  {terminalText(terminal)}
                </pre>
              </section>
            ))
          ) : (
            <p className="text-muted">
              No terminal activity has been reported.
            </p>
          ))}
        {tab === 'debug' && (
          <>
            <p className="mb-3 break-all text-faint">
              Session: {sessionId}
              <br />
              Replay position: {snapshot.lastSeq}
              <br />
              Connection: {snapshot.socket}
            </p>
            <details>
              <summary>Capabilities</summary>
              <StructuredValue value={acp.capabilities} />
            </details>
            <details>
              <summary>Extensions ({acp.extensions.length})</summary>
              <StructuredValue value={acp.extensions} />
            </details>
            <details>
              <summary>Connection log</summary>
              <pre className="whitespace-pre-wrap break-all">
                {bridge.stderr.join('\n')}
              </pre>
            </details>
          </>
        )}
      </div>
    </Panel>
  )
}
